<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { FairMegaMenuBar } from '@/ui/patterns/FairMegaMenu'
import type {
  FairMegaMenuBrandFeatured,
  FairMegaMenuContent,
  FairMegaMenuLink,
} from '@/ui/tokens/fair-mega-menu.data'
import {
  getFairHomeMegaMenuPanels,
  getFairHomeMegaMenuTabs,
} from './fair-home-i18n'
import { getFairHomeCapsulesMegaMenuSize } from './fair-home-capsules.data'
import { FAIR_HOME_STICKY_DISSOLVE_MS } from './fair-home-sticky.data'
import { navigateFairMegaMenuSelectionLink } from './navigateFairMegaMenuSelectionLink'
import {
  navigateFairBrandDetail,
  navigateFairMegaMenuBrandFeatured,
  navigateFairMegaMenuBrandLetter,
  navigateFairMegaMenuBrandLink,
  navigateFairMegaMenuBrandsViewAll,
} from '@/widgets/brands/fair'
import './fair-home-chrome.css'

const props = withDefaults(
  defineProps<{
    handedOff?: boolean
  }>(),
  {
    handedOff: false,
  },
)

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const openMegaTab = ref<string | null>(null)
const megaMenuTabs = computed(() => getFairHomeMegaMenuTabs(t))
const megaMenuPanels = computed(() => getFairHomeMegaMenuPanels(t))
const menuEl = ref<HTMLElement | null>(null)
const viewportWidth = ref(
  typeof window !== 'undefined' ? window.innerWidth : 1920,
)

defineExpose({ menuEl })

const megaMenuSize = computed(() =>
  getFairHomeCapsulesMegaMenuSize(viewportWidth.value),
)

const audience = computed(() => {
  const value = route.query.audience
  if (Array.isArray(value)) {
    return typeof value[0] === 'string' ? value[0] : undefined
  }
  return typeof value === 'string' ? value : undefined
})

const rootStyle = computed(() => ({
  '--fair-home-chrome-chips-dissolve-ms': `${FAIR_HOME_STICKY_DISSOLVE_MS}ms`,
}))

watch(
  () => props.handedOff,
  (handedOff) => {
    if (handedOff) openMegaTab.value = null
  },
)

function handleResize() {
  viewportWidth.value = window.innerWidth
}

function handleLinkClick(link: FairMegaMenuLink) {
  if (navigateFairMegaMenuSelectionLink(router, link, audience.value)) {
    return
  }
  navigateFairMegaMenuBrandLink(router, link, audience.value)
}

function handleViewAllClick(
  section: 'left' | 'right',
  type: FairMegaMenuContent['type'],
) {
  if (type !== 'brands' || section !== 'left') return
  navigateFairMegaMenuBrandsViewAll(router, audience.value)
}

function handleLetterClick(letter: string) {
  navigateFairMegaMenuBrandLetter(router, letter, audience.value)
}

function handleBrandFeaturedClick(item: FairMegaMenuBrandFeatured) {
  navigateFairMegaMenuBrandFeatured(router, item, audience.value)
}

onMounted(() => {
  window.addEventListener('resize', handleResize, { passive: true })
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize)
})
</script>

<template>
  <div
    ref="menuEl"
    class="fair-home-chrome-chips"
    :class="{ 'fair-home-chrome-chips--handed-off': props.handedOff }"
    :style="rootStyle"
    data-sticky-sentinel="capsules-menu"
  >
    <FairMegaMenuBar
      v-model="openMegaTab"
      variant="chips"
      :tabs="megaMenuTabs"
      :panels="megaMenuPanels"
      :size="megaMenuSize"
      @link-click="handleLinkClick"
      @view-all-click="handleViewAllClick"
      @letter-click="handleLetterClick"
      @brand-featured-click="handleBrandFeaturedClick"
    />
  </div>
</template>
